import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import client from '../../api/client';
import Navbar from '../../components/Navbar';
import BoundingBoxCanvas from '../../components/canvas/BoundingBoxCanvas';
import BoxList from '../../components/canvas/BoxList';

export default function LabelCanvas() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [skipping, setSkipping] = useState(false);

  useEffect(() => {
    setBoxes([]);
    setLoadError('');
    setActionError('');
    client.get(`/api/tasks/${taskId}/`)
      .then(res => setTask(res.data))
      .catch(() => setLoadError('Failed to load task. It may no longer be assigned to you.'));
  }, [taskId]);

  const fetchNext = async () => {
    try {
      const res = await client.get('/api/tasks/next/');
      navigate(`/work/${res.data.task_id}`, { state: res.data });
    } catch (e) {
      navigate('/work');
    }
  };

  const handleSkip = async () => {
    setSkipping(true);
    setActionError('');
    try {
      await client.post(`/api/tasks/${taskId}/skip/`);
      await fetchNext();
    } catch {
      setActionError('Failed to skip task.');
      setSkipping(false);
    }
  };

  const handleSubmit = async () => {
    if (boxes.length === 0) {
      setActionError('Draw at least one bounding box before submitting.');
      return;
    }
    setSubmitting(true);
    setActionError('');
    try {
      await client.post(`/api/tasks/${taskId}/submit/`, { boxes });
      navigate('/work');
    } catch (e) {
      setActionError(e.response?.data?.detail || 'Failed to submit annotation.');
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div style={styles.page}>
        <Navbar />
        <div style={styles.errorPage}>
          <p style={styles.errorMsg}>{loadError}</p>
          <button style={styles.backBtn} onClick={() => navigate('/work')}>Back to Queue</button>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div style={styles.page}>
        <Navbar />
        <p style={styles.loading}>Loading task…</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.workspace}>
        <div style={styles.canvasCol}>
          <BoundingBoxCanvas
            imageUrl={task.signed_image_url}
            labelClasses={task.label_classes}
            boxes={boxes}
            onBoxesChange={setBoxes}
          />
        </div>
        <div style={styles.sidebar}>
          <h3 style={styles.sideTitle}>Annotations</h3>
          <BoxList
            boxes={boxes}
            onDelete={idx => setBoxes(prev => prev.filter((_, i) => i !== idx))}
          />
          {task.instructions ? (
            <div style={styles.instructions}>
              <h4 style={styles.instrTitle}>Instructions</h4>
              <p style={styles.instrText}>{task.instructions}</p>
            </div>
          ) : null}
        </div>
      </div>

      <div style={styles.bottomBar}>
        {actionError && <span style={styles.actionError}>{actionError}</span>}
        <div style={styles.barButtons}>
          <button
            style={{ ...styles.barBtn, ...styles.skipBtn }}
            onClick={handleSkip}
            disabled={skipping || submitting}
          >
            {skipping ? 'Skipping…' : 'Skip'}
          </button>
          <button
            style={{ ...styles.barBtn, ...styles.submitBtn }}
            onClick={handleSubmit}
            disabled={submitting || skipping}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#1a1a2e', fontFamily: 'sans-serif', display: 'flex', flexDirection: 'column' },
  workspace: { display: 'flex', flex: 1, overflow: 'hidden' },
  canvasCol: { flex: 1, overflow: 'auto', padding: '1rem', minWidth: 0 },
  sidebar: {
    width: '260px', minWidth: '260px', background: '#16213e', padding: '1rem',
    overflowY: 'auto', borderLeft: '1px solid #0f3460', display: 'flex', flexDirection: 'column', gap: '1rem',
  },
  sideTitle: { margin: 0, color: '#e0e0e0', fontSize: '1rem', fontWeight: 600 },
  instructions: { background: '#0f3460', borderRadius: '6px', padding: '0.75rem' },
  instrTitle: { margin: '0 0 0.5rem', color: '#a0b0c8', fontSize: '0.85rem', fontWeight: 600 },
  instrText: { margin: 0, color: '#c0cfe0', fontSize: '0.85rem', lineHeight: 1.5 },
  bottomBar: {
    background: '#16213e', borderTop: '1px solid #0f3460',
    padding: '0.75rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '1rem',
  },
  actionError: { color: '#E74C3C', fontSize: '0.88rem', flex: 1 },
  barButtons: { display: 'flex', gap: '0.75rem' },
  barBtn: { padding: '0.55rem 1.5rem', border: 'none', borderRadius: '6px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' },
  skipBtn: { background: '#34495e', color: '#fff' },
  submitBtn: { background: '#2ECC71', color: '#fff' },
  loading: { color: '#aaa', padding: '2rem' },
  errorPage: { padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'flex-start' },
  errorMsg: { color: '#E74C3C', fontSize: '1rem' },
  backBtn: { padding: '0.5rem 1rem', background: '#2c3e50', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' },
};
